import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import Zone from "@insite/client-framework/Components/Zone";
import Logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryDepthLoaded,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { SubMenuLink } from "@tecumseh/Overrides/Widgets/Header/MainNavigation";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import isEqual from "lodash/isEqual";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    parentLink = "parentLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.parentLink]: LinkFieldValue;
    };
}

export interface MappedLink {
    id?: string;
    title: string;
    url: string;
    type?: string;
    excludeFromNavigation?: boolean;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    smallImagePath?: string;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    isBackground?: boolean;
    customMenuTitle?: string;
    isCustomMenu?: boolean;
    subMenuLinks?: SubMenuLink[];
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { parentLink },
    } = ownProps;
    const categoryIdsToLoad: Dictionary<number> = {};

    const { type, value } = parentLink;

    let mappedLink: MappedLink | undefined;

    if (type === "Url") {
        mappedLink = {
            url: value,
            title: "",
        };
    } else if (type === "Page") {
        const readonlyPageLink = getPageLinkByNodeId(state, value);
        if (readonlyPageLink) {
            mappedLink = { ...readonlyPageLink };
        }
    } else if (type === "Category") {
        mappedLink = setupCategoryLink(state, value, categoryIdsToLoad, 1, "", state.data.categories.categoryIdsCalled);
    }

    return {
        link: mappedLink,
        location: getLocation(state),
        categoryIdsToLoad,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        categoryIdsCalled: state.data.categories.categoryIdsCalled,
        categoryId: value,
        page: getCurrentPage(state),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

const getParameter = (categoryId: string, maxDepth: number) => ({
    maxDepth,
    startCategoryId: categoryId === emptyGuid ? undefined : categoryId,
    includeStartCategory: categoryId !== emptyGuid,
    expand: ["properties"]
});

const setupCategoryLink = (
    state: ApplicationState,
    value: string,
    categoryIdsToLoad: Dictionary<number>,
    maxDepth: number,
    overrideTitle: string,
    categoryIdsCalled: string[],
) => {
    let mappedLink: MappedLink | undefined;
    if (!value) {
        return;
    }
    const depthLoaded = getCategoryDepthLoaded(state, value);
    const subCategoryIds = getSubCategoryIds(state, value);

    if (
        (depthLoaded < maxDepth || (!subCategoryIds && !categoryIdsCalled?.includes(value))) &&
        !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
    ) {
        categoryIdsToLoad[value] = maxDepth;
    } else {
        if (value === emptyGuid) {
            mappedLink = {
                url: "",
                title: overrideTitle || translate("Products"),
            };
        } else {
            const categoryState = getCategoryState(state, value);
            if (!categoryState.value) {
                if (
                    !categoryState.isLoading &&
                    !getCategoriesDataView(state, getParameter(value, maxDepth)).isLoading
                ) {
                    categoryIdsToLoad[value] = maxDepth;
                }
                return;
            }
            mappedLink = {
                url: categoryState.value.path,
                title: overrideTitle || categoryState.value.shortDescription,
            };
        }

        loadSubCategories(mappedLink, subCategoryIds, 1, maxDepth, state);
    }
    return mappedLink;
};

const loadSubCategories = (
    mappedLink: MappedLink,
    subCategoryIds: readonly string[] | undefined,
    currentDepth: number,
    maxDepth: number,
    state: ApplicationState,
) => {
    if (currentDepth > maxDepth) {
        return;
    }
    mappedLink.children = [];
    if (!subCategoryIds) {
        return;
    }
    for (const subCategoryId of subCategoryIds) {
        const subCategory = getCategoryState(state, subCategoryId).value;
        if (!subCategory) {
            Logger.error(`Category ${subCategoryId} was not found in data state, excluding from navigation.`);
            continue;
        }
        const childLink: MappedLink = {
            url: subCategory.path,
            title: subCategory.shortDescription,
            children: [],
        };
        mappedLink.children.push(childLink);

        loadSubCategories(childLink, subCategory.subCategoryIds, currentDepth + 1, maxDepth, state);
    }
};

export interface SideBarStyles {
    wrapper?: InjectableCss;
    listWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    listTitle?: TypographyPresentationProps;
    list?: InjectableCss;
    listItem?: InjectableCss;
}

export const sideBarStyles: SideBarStyles = {
    wrapper: {
        css: css`
            background: rgba(17, 41, 69, 0.027);
            padding: 20px 15px;
            position: relative;
            width: 100%;
            &::after {
                content: "";
                position: absolute;
                bottom: 0;
                right: 0;
                -webkit-clip-path: polygon(100% 0, 0% 100%, 100% 100%);
                clip-path: polygon(100% 0, 0% 100%, 100% 100%);
                width: 30px;
                height: 30px;
                background-color: ${COLORS.white};
            }
        `,
    },
    listWrapper: {
        css: css`
            span {
                &:hover {
                    color: ${getColor("primary")} !important;
                }
            }
        `,
    },
    listTitle: {
        weight: 600,
        css: css`
            color: ${getColor("secondary")};
            text-transform: capitalize;
            margin-bottom: 15px;
            font-size: ${TYPOGRAPHY.size.larger};
            position: relative;
            > .titleActiveBar {
                background: ${getColor("primary")};
                width: 28px;
                height: 3px;
                display: inline-block;
                transform: rotateZ(-48deg);
                margin-bottom: 6px;
                margin-left: 2px;
            }
        `,
    },
    link: {
        typographyProps: {
            variant: "headerTertiary",
        },
    },
};

const styles = sideBarStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export class SideBar extends React.Component<Props> {
    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (!isEqual(this.props.categoryIdsToLoad, prevProps.categoryIdsToLoad)) {
            this.loadCategoriesIfNeeded(true);
        }
    }

    componentDidMount() {
        this.loadCategoriesIfNeeded();
    }

    loadCategoriesIfNeeded(isComponentDidUpdate = false) {
        const { categoryIdsToLoad, categoryIdsCalled, categoryId, categoryErrorStatusCodeById, loadCategories } =
            this.props;
        if (!categoryErrorStatusCodeById?.[categoryId]) {
            const maxDepth = categoryIdsToLoad[categoryId];
            if (!categoryIdsCalled?.includes(categoryId) || isComponentDidUpdate) {
                loadCategories(getParameter(categoryId, maxDepth));
            }
        }
    }

    render() {
        const { link, location, page } = this.props;

        if (!link) {
            return null;
        }

        const currentPageTitle = page.fields.title.split(" ").join("").toLowerCase();

        const children = link?.children || [];

        const urlArr = location.pathname.toLowerCase().split("/");

        const StyledLink = styled(Link)<{ isActiveLink: boolean }>`
            margin-bottom: 8px;
            position: relative;
            [class^="TypographyStyle"] {
                font-weight: 500;
                font-size: 15px;
                &:hover {
                    color: ${getColor("primary")};
                }
                color: ${props => (props.isActiveLink ? getColor("primary") : COLORS.black)};
                .activeBar {
                    background: ${getColor("primary")};
                    width: 21px;
                    height: 2px;
                    display: inline-block;
                    transform: rotateZ(-48deg);
                    margin-bottom: 6px;
                    margin-left: 2px;
                }
            }
        `;

        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper className="list-wrapper" {...styles.listWrapper}>
                    <Typography as="h3" {...styles.listTitle}>
                        {link.title.toLowerCase()}
                        <span className={link.title.toLowerCase() === currentPageTitle ? "titleActiveBar" : ""}></span>
                    </Typography>
                    <ul {...styles.list}>
                        {children.map(item => {
                            const title = item.title.split(" ").join("").toLowerCase();
                            return (
                                <li key={item.id || item.title}>
                                    <StyledLink isActiveLink={title === currentPageTitle} href={item.url}>
                                        {item.title}
                                        <span className={title === currentPageTitle ? "activeBar" : ""}></span>
                                    </StyledLink>
                                </li>
                            );
                        })}
                    </ul>
                </StyledWrapper>
                <StyledWrapper>
                    <Zone contentId={this.props.id} zoneName="ContentBlock" />
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SideBar),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: "parentLink",
                editorTemplate: "LinkField",
                fieldType: "General",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                allowUrls: () => false,
                isRequired: true,
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
